import { useCallback, useRef, useMemo, useState, type Dispatch } from "react";
import { useRouter } from "@/routing/navigation";
import { ROUTES } from "@/routes/routes";
import {
  isThreadEmbedLocation,
  postStudyEmbedMessage,
  STUDY_EMBED_SOURCE,
} from "@/features/studies/embed/studyEmbedBridge";
import {
  type AudienceSelectionRef,
  SurveyJson,
  type QuestionnaireReviewOptions,
  billableUnitsForDesign,
  billableUnitsForQuestionnaireDraft,
  countStimulusCuts,
  evaluateStudyDesign,
  maxRespondentsForDesign,
  studyCreditCost,
} from "@digital-twins/shared-types";
import { usersApi } from "@/lib/api";
import { useAuth } from "@/lib/config/auth-provider";
import { toast } from "sonner";
import i18n from "@/i18n";
import {
  CREATION_CONSTANTS,
  pickedAudienceOf,
  reviewedPoolOf,
} from "../state/creationState";

const RESUME_DRAFT_STORAGE_KEY = "dtfg_resume_draft_pending";
const CREATION_STORAGE_KEY = "study_creation_state";
import type {
  CreationState,
  CreationAction,
  ReviewPin,
} from "../state/creationState";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import {
  compileQuestionnaireDraftApi,
  getQuestionnaireDraftApi,
  launchQuestionnaireDraftApi,
  updateQuestionnaireDraftApi,
} from "@/features/studies/questionnaire/api/questionnaireDraftsApi";
import {
  buildDraftAudience,
  draftNamesNoPopulation,
} from "@/features/studies/questionnaire/utils/draftAudience";
import { reviewEntryRefusal, type ReviewCompile } from "../reviewEntry";
import {
  requestFailureLine,
  signInActionFor,
  transportFailureCopy,
} from "@/features/studies/questionnaire/utils/questionnaireIssueCopy";
import { STUDY_CREATION_TYPE, STUDY_CREATION_TYPE_ID } from "../types";
import { getEffectiveRespondentLimits } from "../utils/respondentLimits";
import { usableStudyName } from "../utils/studyName";
import { withDefaultStudyExecutionMode } from "@/lib/study-execution-mode";
import { revalidateRail } from "@/lib/chat/revalidateRail";
import { mutate } from "swr";
import {
  SIDEBAR_THREADS_KEY,
  mapSidebarThreadsCache,
} from "@/components/layout/useSidebarThreads";
import { useReviewDraftWrites } from "./useReviewDraftWrites";
import type { LaunchOutcome } from "../launchOutcome";

export interface StudyActionHandlers {
  /**
   * The one way into Review (every route: the builder's Review, the return
   * after a re-plan, a duplicate's "launch"). Writes the picked audience when
   * the draft names none, applies the backend's verdict, and only then pins
   * Review and opens it. `compiled` is a compile the caller already holds;
   * without one the draft is compiled here. Resolves false when Review stays
   * shut, having said why once.
   */
  enterReview: (compiled?: ReviewCompile) => Promise<boolean>;
  /**
   * Launches the draft once Review's writes settle, at the pin they left
   * (each write re-pins). The Launch button and the toast's Retry both
   * come here.
   */
  handleSubmit: () => Promise<LaunchOutcome>;
  /** A rename the user typed in the "Study name" field (not yet saved). */
  editStudyName: (name: string) => void;
  /**
   * Saves the typed rename as the draft's display name (blur or Enter).
   * Resolves false when the save failed; the rename stays pending and the
   * next commit, or launch, sends it again.
   */
  commitStudyName: () => Promise<boolean>;
  /**
   * A typed rename is not saved yet (typed, saving, or its save failed).
   * The page asks before it is left (L13-03): a reload in the field never
   * blurs it, so the rename was dropped with no word.
   */
  renameUnsaved: boolean;
  /**
   * The user changed Review's launch options (quick study, deck, twin
   * chat): the flow takes them, and the draft stores them
   * (`draft.reviewOptions`), so a reload restores them. Launch waits for the
   * write.
   */
  changeReviewOptions: (change: Partial<QuestionnaireReviewOptions>) => void;
}

export function useStudyActions(
  state: CreationState,
  dispatch: Dispatch<CreationAction>,
): StudyActionHandlers {
  const router = useRouter();
  const { refreshProfile } = useAuth();
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);
  // The draft row owns the display name; only an explicit rename writes it.
  // `pendingNameRef` is a typed name not yet saved, `nameCommitRef` the save
  // in flight. A name the field shows from the draft's title or the row is
  // never written back.
  const pendingNameRef = useRef<string | null>(null);
  const nameCommitRef = useRef<Promise<boolean> | null>(null);
  const [renameUnsaved, setRenameUnsaved] = useState(false);

  const editStudyName = useCallback(
    (name: string) => {
      pendingNameRef.current = name;
      setRenameUnsaved(true);
      dispatch({ type: "SET_STUDY_NAME", payload: name });
    },
    [dispatch],
  );

  const commitStudyName = useCallback(async (): Promise<boolean> => {
    const typed = pendingNameRef.current;
    if (typed === null) {
      return (await nameCommitRef.current) ?? true;
    }
    pendingNameRef.current = null;
    const displayName = usableStudyName(typed);
    const draftId = state.questionnaireDraftId;
    // An empty field is no name: launch stays disabled until one is typed.
    if (!displayName || !draftId) {
      setRenameUnsaved(false);
      return true;
    }
    const commit = updateQuestionnaireDraftApi(draftId, { displayName }).then(
      () => {
        void mutate(
          SIDEBAR_THREADS_KEY,
          (current) =>
            mapSidebarThreadsCache(current, (threads) =>
              threads.map((item) =>
                item.kind === "study" && item.token === draftId
                  ? { ...item, title: displayName }
                  : item,
              ),
            ),
          { revalidate: false },
        );
        // A rename typed while this one saved is still unsaved.
        if (pendingNameRef.current === null) {
          setRenameUnsaved(false);
        }
        return true;
      },
      () => {
        if (pendingNameRef.current === null) {
          pendingNameRef.current = typed;
        }
        toast.error(i18n.t("creation.saveNameFailed", { ns: "app" }));
        return false;
      },
    );
    nameCommitRef.current = commit;
    return commit;
  }, [state.questionnaireDraftId]);

  const enterReview = useCallback(
    async (compiled?: ReviewCompile): Promise<boolean> => {
      const draftId = state.questionnaireDraftId;
      if (!state.selectedStudyType || !draftId) {
        return false;
      }
      let entry: ReviewCompile;
      try {
        // The draft as stored: the caller's compile, or a read (a copy that
        // names no population may not compile yet).
        const stored = compiled
          ? { audience: compiled.audience, revision: compiled.revision }
          : await readForReview(draftId);
        // A copy's audience is chosen in the wizard (B-c): the pick is written
        // before Review, as the builder writes it when it opens the copy.
        const picked = pickedAudienceOf(state);
        let writesPick = false;
        if (
          picked &&
          stored.revision !== null &&
          draftNamesNoPopulation(stored.audience)
        ) {
          await updateQuestionnaireDraftApi(draftId, {
            audience: buildDraftAudience(picked),
            expectedRevision: stored.revision,
          });
          writesPick = true;
        }
        entry =
          compiled && !writesPick
            ? compiled
            : {
                ...(await compileForReview(draftId)),
                ...(compiled ? { title: compiled.title } : {}),
              };
      } catch (error) {
        console.error("Failed to prepare the draft for review:", error);
        toast.error(i18n.t("creation.prepareReviewFailed", { ns: "app" }));
        return false;
      }

      const refusal = reviewEntryRefusal(entry, draftId);
      if (refusal !== null || !entry.surveyJson || entry.revision === null) {
        toast.error(refusal ?? i18n.t("questionnaire.reviewRetry", { ns: "app" }));
        return false;
      }

      dispatch({
        type: "SET_SURVEY_JSON",
        payload: entry.surveyJson,
        revision: entry.revision,
        audience: entry.audience,
      });

      // The field shows the draft's own title until the user renames it; the
      // seed is display only and never saved as the display name (launch
      // falls back to the same title). An untitled draft leaves the field
      // empty for the user to name, never the study type's title.
      const draftTitle = usableStudyName(entry.title);
      if (!usableStudyName(state.studyName) && draftTitle) {
        dispatch({ type: "SET_STUDY_NAME", payload: draftTitle });
      }

      dispatch({ type: "SET_STEP", payload: 3 });
      return true;
    },
    [dispatch, state],
  );

  const buildFinalSurveyJson = (): SurveyJson | null => {
    if (!state.editedSurveyJson || !state.selectedStudyType) return null;
    return state.editedSurveyJson;
  };

  function openLaunchedStudy(studyToken: string) {
    postStudyEmbedMessage({
      source: STUDY_EMBED_SOURCE,
      type: "launched",
      studyToken,
    });
    router.push(ROUTES.studyResults(studyToken));
  }

  const repinReview = useCallback(
    (repin: { from: number; revision: number; audience: AudienceSelectionRef }) =>
      dispatch({
        type: "REPIN_REVIEW",
        from: repin.from,
        revision: repin.revision,
        audience: repin.audience,
      }),
    [dispatch],
  );
  // Sample size is not the audience pool. Once review is open, the number on
  // screen is the one launch will send, so keep it on the draft (RND-2794).
  // It and the launch options are Review's writes: each re-pins Review, and
  // launch waits for them.
  const { writeReviewOptions, launchAfterReviewWritesSettle } = useReviewDraftWrites({
    currentStep: state.currentStep,
    questionnaireDraftId: state.questionnaireDraftId,
    numRespondents: state.numRespondents,
    reviewedRevision: state.reviewedRevision,
    reviewedAudience: state.reviewedAudience,
    onRepin: repinReview,
  });

  // The options as the user last set them, read between renders too: a
  // second change before a render keeps the first.
  const reviewOptionsRef = useRef<QuestionnaireReviewOptions>({
    studyMode: state.studyMode,
    deckEnabled: state.deckEnabled,
    twinChatEnabled: state.twinChatEnabled,
  });
  reviewOptionsRef.current = {
    studyMode: state.studyMode,
    deckEnabled: state.deckEnabled,
    twinChatEnabled: state.twinChatEnabled,
  };
  const changeReviewOptions = useCallback(
    (change: Partial<QuestionnaireReviewOptions>) => {
      const next = { ...reviewOptionsRef.current, ...change };
      reviewOptionsRef.current = next;
      if (change.studyMode !== undefined) {
        dispatch({ type: "SET_STUDY_MODE", payload: change.studyMode });
      }
      if (change.deckEnabled !== undefined) {
        dispatch({ type: "SET_DECK_ENABLED", payload: change.deckEnabled });
      }
      if (change.twinChatEnabled !== undefined) {
        dispatch({ type: "SET_TWIN_CHAT_ENABLED", payload: change.twinChatEnabled });
      }
      writeReviewOptions(next);
    },
    [dispatch, writeReviewOptions],
  );

  /**
   * One launch at Review's settled pin. Every exit returns its outcome; a
   * refusal's toast is shown once, at its exit.
   */
  const submitAtPin = async (pin: ReviewPin): Promise<LaunchOutcome> => {
    dispatch({ type: "SET_LOADING", payload: true });
    const refused = (code: string): LaunchOutcome => {
      dispatch({ type: "SET_LOADING", payload: false });
      return { kind: "refused", code };
    };

    const surveyJson = buildFinalSurveyJson();
    const reviewedRevision = pin.revision;
    if (!surveyJson || reviewedRevision === null) {
      toast.error(
        i18n.t("creation.surveyDataMissing", { ns: "app" }),
      );
      return refused("survey_data_missing");
    }

    if (state.selectedStudyType?.id !== STUDY_CREATION_TYPE_ID) {
      toast.error(i18n.t("creation.unsupportedStudyType", { ns: "app" }), {
        description: i18n.t("creation.unsupportedStudyTypeBody", { ns: "app" }),
      });
      return refused("unsupported_study_type");
    }

    if (!state.questionnaireDraftId) {
      dispatch({ type: "SET_STEP", payload: 2 });
      toast.error(i18n.t("creation.studySessionExpired", { ns: "app" }), {
        description: i18n.t("creation.studySessionExpiredBody", { ns: "app" }),
        duration: Infinity,
      });
      return refused("study_session_expired");
    }

    const studyType = state.selectedStudyType ?? STUDY_CREATION_TYPE;
    // The reviewed draft's audience: the one Review priced and launch runs.
    const pool = reviewedPoolOf(state);
    const cutCount = countStimulusCuts(state.editedSurveyJson);
    const limits = getEffectiveRespondentLimits(studyType, {
      filteredCount: pool.filteredCount,
      personaCount: pool.population?.personaCount,
      designMax: maxRespondentsForDesign(state.design),
    });
    const maxRespondents =
      state.studyMode === "quick"
        ? Math.min(limits.max, CREATION_CONSTANTS.QUICK_MODE_MAX_RESPONDENTS)
        : limits.max;
    const respondentCount = state.numRespondents;
    if (respondentCount == null) {
      toast.error(i18n.t("creation.enterRespondents", { ns: "app" }), {
        description: i18n.t("creation.enterRespondentsBody", {
          ns: "app",
          min: limits.min,
          max: maxRespondents,
        }),
      });
      return refused("respondents_missing");
    }
    if (respondentCount < limits.min || respondentCount > maxRespondents) {
      toast.error(i18n.t("creation.invalidRespondents", { ns: "app" }), {
        description: i18n.t("creation.invalidRespondentsBody", {
          ns: "app",
          min: limits.min,
          max: maxRespondents,
        }),
      });
      return refused("respondents_out_of_range");
    }

    const designDecision = evaluateStudyDesign({
      requestedDesign: state.design,
      cutCount,
      numRespondents: respondentCount,
      personaCount: pool.population?.personaCount ?? null,
    });
    if (designDecision.refusal) {
      toast.error(i18n.t("creation.cantLaunchDesign", { ns: "app" }), {
        description: designDecision.refusal.message,
      });
      return refused(designDecision.refusal.code);
    }

    try {
      const freshCredits = await usersApi.getCreditInfo();
      dispatch({ type: "SET_CREDIT_INFO", payload: freshCredits });

      const costPerRespondent = pool.population?.creditsPerRespondent ?? 1;
      const totalCost = studyCreditCost({
        creditsPerRespondent: costPerRespondent,
        billableUnits:
          state.editedSurveyJson != null
            ? billableUnitsForQuestionnaireDraft(
                state.editedSurveyJson as Record<string, unknown>,
                state.design,
                respondentCount,
                cutCount,
              )
            : billableUnitsForDesign(
                state.design,
                respondentCount,
                cutCount,
              ),
        studyMode: state.studyMode === "quick" ? "quick" : "full",
      });

      if (freshCredits.available < totalCost) {
        const allowOverage = freshCredits.allowOverage ?? false;
        if (!allowOverage) {
          toast.error(i18n.t("creation.insufficientCredits", { ns: "app" }), {
            description: i18n.t("creation.insufficientCreditsBody", {
              ns: "app",
              needed: totalCost,
              available: freshCredits.available,
            }),
          });
          return refused("insufficient_credits");
        }
        const overageNeeded = totalCost - freshCredits.available;
        toast.info(i18n.t("creation.usingOverage", { ns: "app" }), {
          description: i18n.t("creation.usingOverageBody", {
            ns: "app",
            count: overageNeeded,
          }),
        });
      }
    } catch {
      // Non-blocking: if credit refresh fails, proceed with server-side check
    }

    // Launch names the experiment from the draft row, so a rename typed just
    // before Launch is saved first; a failed save does not launch.
    // Its own toast says the name wasn't saved.
    if (!(await commitStudyName())) {
      return refused("study_name_not_saved");
    }

    try {
      const launched = await launchQuestionnaireDraftApi(
        state.questionnaireDraftId,
        // The design and the goal are the draft's StudySpec's, which the
        // launch reads (plan Stage 3, S3.4 rows 25 and 26): not sent.
        withDefaultStudyExecutionMode({
          // The revision Review compiled: the survey and price shown.
          expectedRevision: reviewedRevision,
          numRespondents: respondentCount,
          samplerType: "batch_probability",
          workers: CREATION_CONSTANTS.DEFAULT_WORKERS,
          idempotencyKey,
          // The population, filter and saved segments are the draft's
          // (`draft.audience`): the launch reads them there, so none is sent
          // (C09-2, L7a part 4).
          storageId: state.selectedStorageId || undefined,
          language: state.contentLanguage || undefined,
          deckEnabled: state.deckEnabled,
          twinChatEnabled: state.twinChatEnabled,
          studyMode: state.studyMode || undefined,
        }),
      );
      // experiment_created is emitted server-side (experiment orchestration) to avoid double-counting.
      sessionStorage.removeItem(CREATION_STORAGE_KEY);
      if (!isThreadEmbedLocation()) {
        try { localStorage.removeItem(RESUME_DRAFT_STORAGE_KEY); } catch { /* ignore */ }
      }
      toast.success(i18n.t("creation.studyCreated", { ns: "app" }));
      void refreshProfile();
      revalidateRail();
      openLaunchedStudy(launched.experimentId);
      return { kind: "launched", experimentId: launched.experimentId };
    } catch (error: unknown) {
      track(AnalyticsEvents.STUDY_CREATION_FAILED, {
        study_type_id: state.selectedStudyType?.id,
        population_id: pool.population?.id ?? state.reviewedAudience?.populationId,
      });
      console.error("Failed to create test:", error);
      // B10: builder copy is a catalog line or a typed transport line, never
      // the server's sentence.
      const body = (
        error as {
          response?: { data?: { code?: unknown; experimentId?: unknown } };
        }
      )?.response?.data;
      // The draft already launched a run: that run is where the user goes.
      if (
        body?.code === "questionnaire_draft_already_launched" &&
        typeof body.experimentId === "string"
      ) {
        openLaunchedStudy(body.experimentId);
        return { kind: "refused", code: "questionnaire_draft_already_launched" };
      }
      // The draft moved past the reviewed revision (a save, or a launch that
      // was handed back): that pin can never launch again. Drop it and go
      // back to the builder, whose Review compiles the draft as it is now
      // and shows its price.
      if (body?.code === "questionnaire_launch_state_changed") {
        dispatch({ type: "DROP_REVIEW_PIN" });
        dispatch({ type: "SET_STEP", payload: 2 });
      }
      // Retry only when nobody can tell whether the launch landed; it sends
      // the same idempotency key, which the backend answers with that launch.
      const outcomeUnknown = transportFailureCopy(error)?.retryable === true;
      toast.error(
        requestFailureLine(
          error,
          i18n.t("creation.failedCreateStudy", { ns: "app" }),
        ),
        {
          duration: Infinity,
          ...(outcomeUnknown
            ? {
                action: {
                  label: i18n.t("audienceCreate.retry", { ns: "app" }),
                  onClick: () => void handleSubmit(),
                },
              }
            : signInActionFor(error)),
        },
      );
      return {
        kind: "refused",
        code: typeof body?.code === "string" ? body.code : "launch_failed",
      };
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  // The latest render's submit: the one from the click closes over that
  // render, and a navigation during the settle wait is caught by the wait.
  const submitRef = useRef(submitAtPin);
  submitRef.current = submitAtPin;

  const handleSubmit = async (): Promise<LaunchOutcome> => {
    dispatch({ type: "SET_LOADING", payload: true });
    const outcome = await launchAfterReviewWritesSettle((pin) =>
      submitRef.current(pin),
    );
    // A second click while one launch runs leaves that launch's state alone.
    if (outcome.kind === "aborted" && outcome.reason === "left_the_draft") {
      dispatch({ type: "SET_LOADING", payload: false });
      toast.error(i18n.t("creation.failedCreateStudy", { ns: "app" }));
    }
    return outcome;
  };

  return {
    enterReview,
    handleSubmit,
    editStudyName,
    commitStudyName,
    renameUnsaved,
    changeReviewOptions,
  };
}

/** The persisted draft's compile, as Review's entry reads it. */
async function compileForReview(draftId: string): Promise<ReviewCompile> {
  const compiled = await compileQuestionnaireDraftApi(draftId);
  return {
    surveyJson: compiled.surveyJson as SurveyJson,
    revision: compiled.revision ?? null,
    readiness: compiled.readiness ?? null,
    audience: compiled.draft?.audience ?? null,
    title: compiled.draft?.title ?? null,
  };
}

async function readForReview(
  draftId: string,
): Promise<{ audience: unknown; revision: number | null }> {
  const stored = await getQuestionnaireDraftApi(draftId);
  return {
    audience: stored.artifacts?.draft?.audience,
    revision: stored.revision ?? null,
  };
}
